// 📄 pages/Proyectos/InterfacesCatalogo.tsx
// Consulta (solo lectura) del Catálogo de Interfaces/Canales dentro de un
// proyecto — vive en /proyecto/:projectId/interfaces, dentro del grupo
// "Herramientas" del sidebar. El catálogo es global (no por proyecto, ver
// catalogoInterfacesService.ts): el CRUD completo vive en /admin >
// Interfaces / Canales; acá cualquier consultor solo lo consulta/filtra/
// copia. Mismo patrón que [[TransaccionesCatalogo.tsx]].
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowLeftRight, Info } from 'lucide-react';
import { InterfaceChannel, getInterfaces, seedCatalogoInterfaces } from '../../services/catalogoInterfacesService.ts';
import { InterfazZ, getInterfacesZ } from '../../services/interfazZService.ts';
import InterfaceCatalogGrid from '../../components/interfaces/InterfaceCatalogGrid.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useProject } from '../../context/ProjectContext.tsx';

type VistaInterfaces = 'todas' | 'estandar' | 'z';

export default function InterfacesCatalogo() {
  const { user, profile } = useAuth();
  const { projectId } = useProject();
  const navigate = useNavigate();
  const esAdmin = profile?.role === 'admin';
  const [vista, setVista] = useState<VistaInterfaces>('todas');
  const [interfaces, setInterfaces] = useState<InterfaceChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Solo un Admin tiene permiso de escritura en catalogoInterfaces (ver
  // firestore.rules) — un consultor común que entre acá con el catálogo
  // vacío NUNCA debe intentar sembrarlo (fallaría con permission-denied).
  const seedIntentado = useRef(false);

  // Interfaces Z de este proyecto — se cargan aparte (colección/servicio
  // distintos, ver ConfiguracionInterfacesZ.tsx donde se administran).
  const [interfacesZ, setInterfacesZ] = useState<InterfazZ[]>([]);
  const [loadingZ, setLoadingZ] = useState(true);
  const [errorZ, setErrorZ] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        let data = await getInterfaces();
        if (data.length === 0 && esAdmin && !seedIntentado.current) {
          seedIntentado.current = true;
          setSembrando(true);
          try {
            await seedCatalogoInterfaces(user?.email || user?.uid || '');
            data = await getInterfaces();
          } finally {
            setSembrando(false);
          }
        }
        setInterfaces(data.filter((i) => i.activo));
      } catch (err) {
        console.error('Error cargando el catálogo de interfaces/canales:', err);
        setError('No se pudo cargar el catálogo de interfaces/canales.');
      } finally {
        setLoading(false);
      }
    })();
  }, [esAdmin, user]);

  useEffect(() => {
    if (!projectId) { setLoadingZ(false); return; }
    setLoadingZ(true);
    getInterfacesZ(projectId)
      .then(setInterfacesZ)
      .catch((err) => {
        console.error('Error cargando las interfaces Z del proyecto:', err);
        setErrorZ('No se pudieron cargar las interfaces Z de este proyecto.');
      })
      .finally(() => setLoadingZ(false));
  }, [projectId]);

  // El grid espera el shape de InterfaceChannel (ver InterfaceCatalogGrid.tsx)
  // — el modelo Z no tiene `activo`/`transaccionRefId` (no aplican a un
  // desarrollo propio del proyecto), así que se completan acá antes de
  // pasarlo.
  const interfacesZAdaptadas = useMemo(
    () => interfacesZ.map((z) => ({
      id: z.id,
      objetoNegocio: z.objetoNegocio,
      solucion: z.solucion,
      plataforma: z.plataforma,
      transaccion: z.transaccion,
      direccion: z.direccion,
      textoBreve: z.textoBreve,
      nombreTecnico: z.nombreTecnico,
      campoBusqueda: z.campoBusqueda,
      palabrasClave: z.palabrasClave,
      transaccionRefId: null,
      descripcion: z.descripcion,
      tecnologia: z.tecnologia,
      middleware: z.middleware,
      activo: true,
      responsable: z.responsable,
      sistemasDisponibles: z.sistemasDisponibles
    })),
    [interfacesZ]
  );

  // El contador siempre refleja el total de Z del proyecto, sin importar el
  // filtro que esté activo DENTRO del grid de Z (ese filtro es estado local
  // de InterfaceCatalogGrid, no llega hasta acá).
  const zCount = interfacesZ.length;

  const OPCIONES_VISTA: { id: VistaInterfaces; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    { id: 'estandar', label: 'Estándar' },
    { id: 'z', label: zCount > 0 ? `Interfaces Z (${zCount})` : 'Interfaces Z' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {OPCIONES_VISTA.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setVista(id)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              vista === id
                ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {vista !== 'z' && (
        <>
          <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Filtra por Solución, Plataforma o Dirección y usa el botón de copiar para llevarte el código de transacción directo al portapapeles.</span>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          {loading ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
            </div>
          ) : interfaces.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Todavía no hay interfaces/canales registrados en el catálogo. Un Admin puede cargarlas desde /admin.</p>
            </div>
          ) : (
            <InterfaceCatalogGrid
              interfaces={interfaces}
              readOnly
              onVerEnTransacciones={() => navigate(`/proyecto/${projectId}/transacciones`)}
            />
          )}
        </>
      )}

      {vista !== 'estandar' && projectId && (
        <div className={vista === 'todas' ? 'pt-6 border-t border-white/10 space-y-4' : 'space-y-4'}>
          <div>
            <h3 className="text-sm font-bold text-white">Interfaces Z de este proyecto</h3>
            <p className="text-xs text-gray-500 mt-0.5">Interfaces/canales personalizados configurados para este proyecto. Solo tú y tu equipo los ven.</p>
          </div>

          {errorZ && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorZ}</div>
          )}

          {loadingZ ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <p className="text-sm">Cargando interfaces Z...</p>
            </div>
          ) : interfacesZ.length === 0 ? (
            <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
              <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-sm">Aún no hay interfaces Z en este proyecto. Agrégalas en Configuración &gt; Desarrollo Z.</p>
            </div>
          ) : (
            <InterfaceCatalogGrid interfaces={interfacesZAdaptadas} esZ readOnly />
          )}
        </div>
      )}
    </div>
  );
}
